import { useForm, useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Share2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { eventImagesApi } from '~/entities/event/api'
import {
  useEvent,
  usePublishEvent,
  useUnlistEvent,
  useUpdateEvent,
} from '~/entities/event/model/use-events'
import { useListings } from '~/entities/listing/model/use-listings'
import { coverSubjectOf } from '~/features/events/ai-flow/cover-chat-button'
import {
  EventDateTimeSection,
  EventDetailsSection,
  EventFeaturesSection,
  EventImagesSection,
  EventLocationSection,
  EventPreviewModal,
  EventPricingSection,
  type RecurringConfig,
} from '~/features/events/components'
import { EventFormLayout } from '~/features/events/components/event-form-layout'
import { applyAddressVisibility } from '~/features/events/lib/address-visibility'
import { buildEventFormValues, type EventFormValues } from '~/features/events/lib/event-form-values'
import {
  applyEventPrivacy,
  privacyStatusAction,
  publishActionFor,
} from '~/features/events/lib/event-privacy'
import { useSavedConfirmationStep } from '~/features/events/lib/use-saved-confirmation'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useLeaveGuard } from '~/features/shared/components/leave-guard'
import { useAfterSaveAction } from '~/features/shared/hooks/use-after-save-action'
import { saveStagedImages } from '~/features/shared/lib/image-order'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { focusFirstInvalidField, submitForm } from '~/shared/lib/form-utils'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { copyToClipboard, getEventShareUrl } from '~/shared/lib/share'
import { storageManager } from '~/shared/lib/storage-manager'
import { useFormPersistence } from '~/shared/lib/use-form-persistence'
import type { Event } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Skeleton } from '~/shared/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'
import { PageHeader } from '~/widgets/page-header'

export const Route = createFileRoute('/dashboard/events/$eventId/edit')({
  component: EditEvent,
})

function EditEvent() {
  const { eventId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const { data: event, isLoading } = useEvent(business?.id || '', eventId)
  const savedConfirmation = useSavedConfirmationStep(business?.id ?? '', eventId)
  const { listings } = useListings(business?.id || '')
  const updateEvent = useUpdateEvent(business?.id || '')
  const publishEvent = usePublishEvent(business?.id || '')
  const unlistEvent = useUnlistEvent(business?.id || '')
  const [images, setImages] = useState<ImageFile[]>([])
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0)
  const [insertPositions, setInsertPositions] = useState<number[]>([])
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([])
  const [isUploadingImages, setIsUploadingImages] = useState(false)

  const linkedListingName = event?.listingId
    ? listings.find((l) => l.id === event.listingId)?.name || null
    : null

  const handleImagesChange = (
    newImages: ImageFile[],
    newPrimaryIndex: number,
    newInsertPositions?: number[],
  ) => {
    setImages(newImages)
    setPrimaryImageIndex(newPrimaryIndex)
    setInsertPositions(newInsertPositions || [])
  }

  const handleFormSubmit = async (
    value: EventFormValues,
    clearFormState: () => void,
    afterSaveAction?: 'publish' | 'unlist',
  ) => {
    try {
      await updateEvent.mutateAsync({
        eventId,
        data: applyEventPrivacy(applyAddressVisibility(value, savedConfirmation)),
      })

      if (images.length > 0) {
        setIsUploadingImages(true)
        try {
          await saveStagedImages(eventImagesApi, eventId, {
            existingIds: (event?.images || []).map((img) => img.id),
            files: images.map((img) => img.file),
            insertPositions,
            removedIds: removedImageIds,
          })

          await queryClient.invalidateQueries({
            queryKey: queryKey(QueryKey.EVENT, eventId),
          })
          await queryClient.invalidateQueries({
            queryKey: queryKey(QueryKey.EVENTS, business?.id),
          })

          toast.success(m.eventUpdatedWithImages(), {
            description: m.yourEventAndImagesHaveBeenUpdatedSuccessfully(),
          })
        } catch (imageError) {
          log.error(
            imageError instanceof Error ? imageError : new Error(String(imageError)),
            'Failed to upload images',
          )
          toast.warning(m.eventUpdatedButSomeImagesFailedToUpload(), {
            description: m.youCanAddImagesLaterFromTheEventDetailsPage(),
          })
        } finally {
          setIsUploadingImages(false)
        }
      } else {
        toast.success(m.eventUpdated(), {
          description: m.yourEventHasBeenUpdatedSuccessfully(),
        })
      }

      clearFormState()

      if (afterSaveAction === 'publish') {
        try {
          await publishEvent.mutateAsync(eventId)
          toast.success(m.eventPublished())
        } catch (publishError) {
          toast.error(m.failedToPublishEvent(), {
            description: getErrorMessageFromError(publishError),
          })
        }
      } else if (afterSaveAction === 'unlist') {
        try {
          await unlistEvent.mutateAsync(eventId)
          toast.success(m.eventUnlisted())
        } catch (unlistError) {
          toast.error(m.failedToUnlistEvent(), {
            description: getErrorMessageFromError(unlistError),
          })
        }
      }

      navigate({ to: '/dashboard/events' })
    } catch (error) {
      log.error(
        error instanceof Error ? error : new Error(String(error)),
        '[EditEvent] Failed to update event',
      )
      toast.error(getErrorMessageFromError(error))
    }
  }

  if (isLoading || !event) {
    if (isLoading) {
      return <EditEventSkeleton />
    }
    return (
      <div className="space-y-6">
        <PageHeader
          title={m.eventNotFound()}
          description={m.theEventYoureLookingForDoesntExist()}
        />
      </div>
    )
  }

  return (
    <EditEventForm
      event={event}
      eventId={eventId}
      handleFormSubmit={handleFormSubmit}
      navigate={navigate}
      updateEvent={updateEvent}
      images={images}
      handleImagesChange={handleImagesChange}
      handleRemovedImagesChange={setRemovedImageIds}
      primaryImageIndex={primaryImageIndex}
      isUploadingImages={isUploadingImages}
      linkedListingName={linkedListingName}
      savedConfirmation={savedConfirmation}
    />
  )
}

interface EditEventFormProps {
  event: Event
  eventId: string
  handleFormSubmit: (
    value: EventFormValues,
    clearFormState: () => void,
    afterSaveAction?: 'publish' | 'unlist',
  ) => Promise<void>
  navigate: ReturnType<typeof useNavigate>
  updateEvent: ReturnType<typeof useUpdateEvent>
  images: ImageFile[]
  handleImagesChange: (
    newImages: ImageFile[],
    newPrimaryIndex: number,
    newInsertPositions?: number[],
  ) => void
  handleRemovedImagesChange: (imageIds: string[]) => void
  primaryImageIndex: number
  isUploadingImages: boolean
  linkedListingName?: string | null
  savedConfirmation?: boolean
}

function EditEventForm({
  event,
  eventId,
  handleFormSubmit,
  navigate,
  updateEvent,
  images,
  handleImagesChange,
  handleRemovedImagesChange,
  primaryImageIndex,
  isUploadingImages,
  linkedListingName,
  savedConfirmation,
}: EditEventFormProps) {
  const afterSave = useAfterSaveAction<'publish' | 'unlist'>()
  const { limits } = usePlan()
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    enabled: false,
    frequencyType: 'weeks',
    interval: 1,
    count: 5,
  })
  const storageKey = `pulsify-edit-event-${eventId}`
  storageManager.removeSession(storageKey)

  const form = useForm({
    defaultValues: buildEventFormValues(event),
    onSubmitInvalid: ({ formApi }) => {
      afterSave.consume()
      focusFirstInvalidField(formApi.state.fieldMeta)
    },
    onSubmit: async ({ value }) => {
      await handleFormSubmit(
        value,
        () => {
          clearFormState()
          leaveGuard.release()
        },
        afterSave.consume() ?? undefined,
      )
    },
  })

  const { clearFormState, hasSavedState } = useFormPersistence(form, {
    storageKey,
  })
  const dirty = useStore(form.store, (state) => state.isDirty)
  const leaveGuard = useLeaveGuard(dirty || images.length > 0, m.leaveUnsavedBody())

  const handleCancel = () => {
    clearFormState()
    navigate({ to: '/dashboard/events' })
  }

  const handleClearDraft = () => {
    form.reset(buildEventFormValues(event))
    clearFormState()
    toast.info(m.changesDiscarded(), {
      description: m.yourUnsavedChangesHaveBeenDiscarded(),
    })
  }

  const isDraft = event.isDraft || event.status === 'Draft'
  const coverImageUrl =
    images.length > 0
      ? images[primaryImageIndex]?.preview
      : event.images?.[0]?.url || event.coverImageUrl || ''
  const hasImages = !!(event.coverImageUrl || event.images?.length > 0 || images.length > 0)

  return (
    <div className="space-y-6">
      {leaveGuard.dialog}
      <div className="mx-auto w-full max-w-7xl">
        <PageHeader
          title={m.editEvent()}
          description={m.updateYourEventInformation()}
          action={
            <div className="flex flex-wrap gap-2">
              {isDraft ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant="outline" disabled>
                          <Share2 className="h-4 w-4 mr-2" />
                          {m.shareLink()}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{m.publishBeforeSharing()}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  variant="outline"
                  onClick={async () => {
                    const ok = await copyToClipboard(getEventShareUrl(eventId))
                    if (ok) {
                      toast.success(m.linkCopied(), {
                        description: m.linkCopiedToClipboard(),
                      })
                    } else {
                      toast.error(m.failedToCopyLink())
                    }
                  }}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {m.shareLink()}
                </Button>
              )}
              <div className="xl:hidden">
                <form.Subscribe
                  selector={(state) => ({
                    title: state.values.title,
                    description: state.values.description,
                    startTime: state.values.occurrences[0]?.date || state.values.startTime,
                    location: state.values.location,
                    price: state.values.price,
                  })}
                >
                  {({ title, description, startTime, location, price }) => (
                    <EventPreviewModal
                      title={title}
                      description={description}
                      startTime={startTime}
                      location={location}
                      price={price}
                      coverImageUrl={coverImageUrl}
                    />
                  )}
                </form.Subscribe>
              </div>
              {hasSavedState() && (
                <Button variant="ghost" size="sm" onClick={handleClearDraft}>
                  {m.discardChanges()}
                </Button>
              )}
              <Button variant="outline" className="sm:hidden" onClick={handleCancel}>
                {m.cancel()}
              </Button>
            </div>
          }
        />
      </div>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          e.stopPropagation()
          await submitForm(form)
        }}
      >
        <EventFormLayout
          form={form}
          coverImageUrl={coverImageUrl}
          onCancel={handleCancel}
          actions={({ isSubmitting, privacy }) => {
            const isDisabled = updateEvent.isPending || isUploadingImages || isSubmitting
            const isBusy = updateEvent.isPending || isSubmitting
            const save = {
              label: m.saveChanges(),
              busyLabel: isUploadingImages ? m.uploadingImages() : m.updating(),
              busy: (isBusy && afterSave.action === null) || isUploadingImages,
              disabled: isDisabled,
            }
            if (!isDraft) {
              const statusAction = privacyStatusAction(privacy, event)
              return {
                primary: {
                  ...save,
                  onClick: () =>
                    statusAction ? afterSave.trigger(form, statusAction) : submitForm(form),
                },
              }
            }
            const goLive = publishActionFor(privacy)
            return {
              primary: {
                label: goLive === 'unlist' ? m.publishPrivately() : m.saveAndPublish(),
                busyLabel: m.savingAndPublishing(),
                busy: isBusy && afterSave.action !== null,
                disabled: isDisabled,
                blockedHint: hasImages ? undefined : m.addACoverImageToPublish(),
                onClick: () => afterSave.trigger(form, goLive),
              },
              secondary: { ...save, label: m.saveAsDraft(), onClick: () => submitForm(form) },
            }
          }}
        >
          <EventImagesSection
            eventId={eventId}
            existingImages={event.images || []}
            onImagesChange={handleImagesChange}
            onRemovedExistingChange={handleRemovedImagesChange}
            coverSubject={() => coverSubjectOf(form.state.values)}
          />
          <EventDetailsSection form={form} />
          <EventDateTimeSection
            form={form}
            recurringConfig={recurringConfig}
            onRecurringConfigChange={setRecurringConfig}
            canRecur={limits.canRecurEvents}
          />
          <EventLocationSection
            form={form}
            isEditMode={true}
            linkedListingName={linkedListingName}
            savedConfirmation={savedConfirmation}
          />
          <EventPricingSection form={form} />
          <EventFeaturesSection form={form} collapsible />
        </EventFormLayout>
      </form>
    </div>
  )
}

function EditEventSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Skeleton className="h-8 w-48 max-w-full mb-2" />
          <Skeleton className="h-4 w-64 max-w-full" />
        </div>
        <Skeleton className="h-10 w-24" />
      </div>

      <div className="mx-auto grid w-full max-w-7xl gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
        <div className="space-y-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-64" />
        </div>
        <Skeleton className="hidden h-96 xl:block" />
      </div>
    </div>
  )
}
